import { isRepeatingGroup } from '../../config/repeatingGroups';
import { isHiddenFromMe } from '../../utils/elementAccess';
import { entriesOf, isHeading, nounsOf, visibleColumns } from '../../utils/repeatingGroupEntries';
import { blockPage, sheetPage } from './pageLayoutForm';

/**
 * A repeating group on a form's ruled lines (KPR-1863, KPE-041): the pure rules of a
 * `group` block. keepr-api docs/SCHEMA.md § Card layouts has its shape.
 *
 * The block's box is the area of the form's table. `lines` divides it, evenly unless
 * `lineTops` says where each line starts, and `columns` place each chosen column
 * from the block's left, so moving the block moves them all. Every number is in the
 * sheet's own unit, as a block's are.
 *
 * WHAT A PERSON ADJUSTS FOR A REAL FORM. A Department's PDF is perfectly even; a scan
 * is not. One line can be moved on its own (`lineTops`), and one drag tilts the whole
 * area to a scan that is not square on the page (`tilt`). A tilt moves each cell (a
 * small-angle rotation of its position) and never rotates it: text stays level, a
 * cell stays upright, and pageBlockFit measures a cell as it measures a block. A CSS
 * rotation would have the browser answer rotated bounding boxes, and a second measure.
 *
 * Coordinates here are never pixels: the editor converts a drag before it comes here
 * (`dragGroup` takes page units). pageLayoutModel.js imports this file for what a
 * resize does to a group, so this file does not import it.
 */

export const MAX_GROUP_LINES = 100;
export const MAX_GROUP_COLUMNS = 24;
export const DEFAULT_GROUP_LINES = 10;
const DEFAULT_COLUMNS = 4;
/** A tenth of the width is almost 6 degrees: the API's ceiling too. */
export const MAX_TILT_SHARE = 0.1;

/**
 * Where a line and a tilt land: finer than a drag of a block, and never on the
 * grid, because they fit what is printed on the form. About a pixel and a half.
 */
export const fineStep = unit => (unit === 'mm' ? 0.2 : 1 / 64);

// The shortest line a drag leaves, and the narrowest column.
const smallest = unit => (unit === 'mm' ? 1 : 0.05);

const places = unit => (unit === 'mm' ? 10 : 10000);
const roundIn = (value, unit) => Math.round(value * places(unit)) / places(unit);
// Rounded down: a far edge never crosses the block's, where the API refuses it.
const floorIn = (value, unit) => Math.floor(value * places(unit) + 1e-9) / places(unit);
const clamp = (value, low, high) => Math.min(Math.max(value, low), high);
const snapped = (value, step) => (step ? Math.round(value / step) * step : value);

/** A group this person may read, or null: gone from the card, retyped, or hidden from them. */
export const readableGroup = def => (isRepeatingGroup(def) && !isHiddenFromMe(def) ? def : null);

/** What a group is called on a notice: its plural, as it reads on the item. */
export const groupName = group => nounsOf(group).pluralLabel;

/** The columns a ruled line can hold: what the person may read, less files, which have no text to print. */
export const printableColumns = group => visibleColumns(group).filter(column => column.dataType !== 'file');

/**
 * The columns this block places that cannot print for this person: the card lost
 * them, they are hidden from this person, or they hold files now. The client cannot
 * tell the first two apart, so callers name none of them and say nothing of why.
 */
export function lostColumns(block, group) {
	const printable = new Set(printableColumns(group).map(column => column.name));
	return (block.columns || []).filter(column => !printable.has(column.name)).map(column => column.name);
}

/** Each line's top, from the block's top: `lineTops`, or the lines evenly spaced. */
export function lineTopsOf(block) {
	const lines = Math.max(1, Number(block.lines) || 1);
	if (Array.isArray(block.lineTops) && block.lineTops.length === lines) return block.lineTops;
	return Array.from({ length: lines }, (_, index) => (index * block.h) / lines);
}

/**
 * One cell on the sheet of its block: `{ left, top, width, height }` from the block's
 * corner. `column` is any `{ x, w }`, so a heading passes the whole block's width.
 */
export function cellBox(block, line, column, tops = lineTopsOf(block)) {
	const top = tops[line];
	const slope = (block.tilt || 0) / block.w;
	return {
		left: column.x - slope * top,
		top: top + slope * column.x,
		width: column.w,
		height: (line + 1 < tops.length ? tops[line + 1] : block.h) - top,
	};
}

/**
 * What of a group's value fits the block's lines. `shown` the entries on the lines,
 * in stored order; the rest counted. A heading takes a line as an entry does, and is
 * not an entry when the person is told how many of them did not print.
 */
export function entriesOnLines(value, lines) {
	const entries = entriesOf(value);
	const rest = entries.slice(lines);
	const headings = rest.filter(isHeading).length;
	return {
		shown: entries.slice(0, lines),
		notShown: rest.length - headings,
		headingsNotShown: headings,
		total: entries.filter(entry => !isHeading(entry)).length,
	};
}

/** The words for what did not print, or '' when everything did. */
export function notShownWords({ notShown, headingsNotShown, total }) {
	const heads = headingsNotShown ? `${headingsNotShown} ${headingsNotShown === 1 ? 'heading' : 'headings'}` : '';
	if (!notShown) return heads ? `${heads} not shown` : '';
	return `${notShown} of ${total} ${total === 1 ? 'entry' : 'entries'}${heads ? ` and ${heads}` : ''} not shown`;
}

/** The first columns of a group, side by side across `w`: what a new block starts with. */
export function defaultGroupFields(group, w, unit) {
	const chosen = printableColumns(group).slice(0, DEFAULT_COLUMNS);
	const width = chosen.length ? floorIn(w / chosen.length, unit) : 0;
	return {
		lines: DEFAULT_GROUP_LINES,
		columns: chosen.map((column, index) => ({ name: column.name, x: roundIn(index * width, unit), w: width })),
	};
}

const fitColumn = (column, width, unit) => {
	const narrow = Math.min(smallest(unit), width);
	const w = clamp(floorIn(column.w, unit), narrow, width);
	return { ...column, w, x: clamp(roundIn(column.x, unit), 0, floorIn(width - w, unit)) };
};

const rising = tops => tops.every((top, index) => index === 0 || top > tops[index - 1]);

/**
 * A group block given a new box. Its columns and uneven lines scale with it, so an
 * area redrawn over a table keeps its proportions, and a tilt keeps its angle. The
 * number of lines does not change. Lines that rounding would stack go back to even.
 */
export function scaleGroup(block, w, h, unit) {
	const wide = w / block.w;
	const tall = h / block.h;
	const next = { ...block, w, h };
	if (wide !== 1) {
		next.columns = (block.columns || []).map(column => fitColumn({ ...column, x: column.x * wide, w: column.w * wide }, w, unit));
		if (block.tilt) {
			const most = floorIn(w * MAX_TILT_SHARE, unit);
			const tilt = clamp(roundIn(block.tilt * wide, unit), -most, most);
			if (tilt) next.tilt = tilt; else delete next.tilt;
		}
	}
	if (tall !== 1 && Array.isArray(block.lineTops)) {
		const tops = block.lineTops.map(top => roundIn(top * tall, unit));
		if (rising(tops) && tops[tops.length - 1] < h) next.lineTops = tops; else delete next.lineTops;
	}
	return next;
}

const withGroup = (body, id, change) => ({
	...body,
	blocks: body.blocks.map(block => (block.id === id && block.type === 'group' ? change(block) : block)),
});

/** A new count, from the inspector: the area stays, and the lines space evenly in it. */
export const setGroupLines = (body, id, lines) => withGroup(body, id, ({ lineTops: _tops, ...block }) => ({
	...block,
	lines: clamp(Math.round(lines) || 1, 1, MAX_GROUP_LINES),
}));

/** Undoes every moved line. */
export const evenGroupLines = (body, id) => withGroup(body, id, ({ lineTops: _tops, ...block }) => block);

/** Undoes a tilt. */
export const straightenGroup = (body, id) => withGroup(body, id, ({ tilt: _tilt, ...block }) => block);

/**
 * The chosen columns, by name, from the inspector (`names`, out of `offered`). One
 * already placed stays where it is. A new one takes the room right of the last, or
 * an even share of the block when there is none, and the person drags it from there.
 * A placed column `offered` does not name (hidden from this person, or lost by the
 * card) is not this list's to take off.
 */
export function setGroupColumns(body, id, names, offered, unit) {
	return withGroup(body, id, (block) => {
		const next = (block.columns || []).filter(column => names.includes(column.name) || !offered.includes(column.name));
		names.forEach((name) => {
			if (next.some(column => column.name === name) || next.length >= MAX_GROUP_COLUMNS) return;
			const end = next.reduce((far, column) => Math.max(far, column.x + column.w), 0);
			const room = block.w - end;
			const fits = room >= smallest(unit);
			next.push(fitColumn({ name, x: fits ? end : 0, w: fits ? room : block.w / (next.length + 1) }, block.w, unit));
		});
		return { ...block, columns: next };
	});
}

/** Takes a column off the block. The only way a lost one goes. */
export const removeGroupColumn = (body, id, name) => withGroup(body, id, block => ({
	...block,
	columns: (block.columns || []).filter(column => column.name !== name),
}));

// ---- Dragging --------------------------------------------------------------

/** The prefix of every handle's drag id. */
export const GROUP_DRAG = 'grp:';

/** A handle's drag id. Encoded, because a column's name or a block's id may hold a colon. */
export const groupDragId = (kind, blockId, target = '') => `${GROUP_DRAG}${kind}:${encodeURIComponent(target)}:${encodeURIComponent(blockId)}`;

/** `{ kind, target, blockId }`. */
export function parseGroupDrag(id) {
	const [, kind, target = '', blockId = ''] = String(id).split(':');
	return { kind, target: decodeURIComponent(target), blockId: decodeURIComponent(blockId) };
}

const dragColumn = (block, name, dx, step, unit) => {
	const columns = (block.columns || []).map((column) => {
		if (column.name !== name) return column;
		// On the sheet's grid, as a block is: the block's corner may be off it.
		const x = step ? snapped(block.x + column.x + dx, step) - block.x : column.x + dx;
		return { ...column, x: clamp(roundIn(x, unit), 0, floorIn(block.w - column.w, unit)) };
	});
	return { ...block, columns };
};

const dragColumnWidth = (block, name, dx, step, unit) => {
	const columns = (block.columns || []).map((column) => {
		if (column.name !== name) return column;
		const far = step ? snapped(block.x + column.x + column.w + dx, step) - block.x : column.x + column.w + dx;
		return { ...column, w: clamp(floorIn(far - column.x, unit), smallest(unit), floorIn(block.w - column.x, unit)) };
	});
	return { ...block, columns };
};

/** Line `index` (from 1: the first line starts at the area's top, which moves with the area) moved alone. */
const dragLine = (block, index, dy, unit) => {
	const tops = [...lineTopsOf(block)];
	if (!(index >= 1 && index < tops.length)) return block;
	const fine = fineStep(unit);
	const low = tops[index - 1] + smallest(unit);
	const high = (index + 1 < tops.length ? tops[index + 1] : block.h) - smallest(unit);
	if (low > high) return block;
	tops[index] = clamp(roundIn(snapped(tops[index] + dy, fine), unit), low, high);
	return { ...block, lineTops: tops.map(top => roundIn(top, unit)) };
};

/** Whole lines added or taken off the foot of the area, each line keeping its height. */
const dragCount = (block, dy, unit, sheetHeight) => {
	const tops = lineTopsOf(block);
	const count = tops.length;
	const lastTop = tops[count - 1];
	const last = block.h - lastTop;
	// As many as reach the sheet's foot, and never fewer than it holds: an area already past the foot stays.
	const room = count - 1 + Math.floor((sheetHeight - block.y - lastTop) / last + 1e-9);
	const lines = clamp(count + Math.round(dy / last), 1, clamp(room, count, MAX_GROUP_LINES));
	if (lines === count) return block;
	if (!Array.isArray(block.lineTops)) return { ...block, lines, h: Math.max(smallest(unit), roundIn(last * lines, unit)) };
	const grown = Array.from({ length: lines }, (_, index) => (index < count ? tops[index] : roundIn(lastTop + last * (index - count + 1), unit)));
	return { ...block, lines, lineTops: grown, h: roundIn(lines < count ? tops[lines] : grown[lines - 1] + last, unit) };
};

const dragTilt = ({ tilt: _tilt, ...block }, was, dy, unit) => {
	const most = block.w * MAX_TILT_SHARE;
	const tilt = clamp(roundIn(snapped(was + dy, fineStep(unit)), unit), -floorIn(most, unit), floorIn(most, unit));
	return tilt ? { ...block, tilt } : block;
};

/**
 * What a handle does, `dx` and `dy` in page units. The preview and the drop both call
 * this, so what is shown is what lands. `step` is the grid, which columns follow.
 */
export function dragGroup(body, id, dx, dy, step = null) {
	const { kind, target, blockId } = parseGroupDrag(id);
	const { unit } = body.page;
	return withGroup(body, blockId, (block) => {
		switch (kind) {
			case 'col': return dragColumn(block, target, dx, step, unit);
			case 'colw': return dragColumnWidth(block, target, dx, step, unit);
			case 'line': return dragLine(block, Number(target), dy, unit);
			case 'count': return dragCount(block, dy, unit, sheetPage(body, blockPage(block)).height);
			case 'tilt': return dragTilt(block, block.tilt || 0, dy, unit);
			default: return block;
		}
	});
}

/** What a screen reader hears while a handle is carried, in the page's own unit. */
export function groupDragNote(body, id) {
	const { kind, target, blockId } = parseGroupDrag(id);
	const block = body.blocks.find(candidate => candidate.id === blockId);
	if (!block) return '';
	const { unit } = body.page;
	const column = (block.columns || []).find(candidate => candidate.name === target);
	switch (kind) {
		case 'col': return column ? `Column at ${column.x} ${unit}.` : '';
		case 'colw': return column ? `Column ${column.w} ${unit} wide.` : '';
		case 'line': return `Line ${Number(target) + 1} starts ${roundIn(lineTopsOf(block)[Number(target)], unit)} ${unit} down.`;
		case 'count': return `${block.lines} lines.`;
		case 'tilt': return block.tilt ? `The right end is ${Math.abs(block.tilt)} ${unit} ${block.tilt > 0 ? 'lower' : 'higher'}.` : 'Level.';
		default: return '';
	}
}
